import { BrowserRouter, Link, Route, Routes } from 'react-router-dom';
import './App.css';
import Contact from './Contact';
import Figure, { bind } from './Figure';
import { figures } from './diagrams';
import {
  project, introduction, researchGap, problem, solution, mainObjective, components,
  methodology, services, architectureNote, evaluation, milestones, team,
  supervisors, affiliation,
} from './content';

const nav = [
  ['introduction', 'Introduction'],
  ['scope', 'Project scope'],
  ['methodology', 'Methodology'],
  ['milestones', 'Milestones'],
  ['about', 'About us'],
];

function Logo() {
  return (
    <span className="logo">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 12c0-4.418 3.582-8 8-8s8 3.582 8 8" />
        <path d="M4 12c0 4.418 3.582 8 8 8s8-3.582 8-8" opacity=".4" />
        <path d="M8 12h8" />
        <path d="M12 8l4 4-4 4" />
      </svg>
    </span>
  );
}

// The five components drawn as the closed loop the research is about.
function Loop() {
  const c = 240;
  const r = 175;
  const at = (deg, radius = r) => {
    const a = (deg * Math.PI) / 180;
    return [c + radius * Math.cos(a), c + radius * Math.sin(a)];
  };
  return (
    <svg className="loop" viewBox="0 0 480 480" role="img" aria-labelledby="loop-title">
      <title id="loop-title">
        The SkillBridge loop: emotion, knowledge gaps, difficulty, exercises and analytics circle the learner.
      </title>
      <circle className="loop-ring" cx={c} cy={c} r={r} />
      {components.map((_, i) => {
        const deg = i * 72 - 54;
        const [x, y] = at(deg);
        return <path key={i} className="loop-arrow" d="M-6,-6 L6,0 L-6,6" transform={`translate(${x} ${y}) rotate(${deg + 90})`} />;
      })}
      <g className="loop-orbit">
        <circle cx={c} cy={c - r} r="7" />
      </g>
      <text className="loop-centre" x={c} y={c - 4}>Learner</text>
      <text className="loop-sub" x={c} y={c + 24}>answers a question</text>
      {components.map((item, i) => {
        const [x, y] = at(i * 72 - 90);
        return (
          <a key={item.id} href={`#${item.id}`} className="loop-node" aria-label={item.name}>
            <circle cx={x} cy={y} r="52" />
            <text x={x} y={y}>{item.short}</text>
          </a>
        );
      })}
    </svg>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="section" aria-labelledby={`${id}-title`}>
      <div className="wrap section-grid">
        <h2 id={`${id}-title`}>{title}</h2>
        <div className="section-body">{children}</div>
      </div>
    </section>
  );
}

// One methodology card. It renders whichever parts the item in content.js carries.
function Method({ item }) {
  const { table } = item;
  return (
    <div className="method">
      <h4>{item.title}</h4>
      {item.what && <p>{item.what}</p>}
      {item.strip && (
        <ol className="flow">
          {item.strip.map(([step, detail]) => (
            <li key={step}><strong>{step}</strong><span>{detail}</span></li>
          ))}
        </ol>
      )}
      {item.expr && item.expr.map((e) => <p key={e} className="expr">{bind(e)}</p>)}
      {item.terms && (
        <ul className="terms">
          {item.terms.map(([term, meaning]) => (
            <li key={term}><strong>{term}:</strong> {meaning}</li>
          ))}
        </ul>
      )}
      {table && (
        <table className="mtable">
          <thead><tr>{table.head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {table.rows.map(([first, ...rest]) => (
              <tr key={first}>
                <th scope="row">{first}</th>
                {rest.map((cell, i) => <td key={table.head[i + 1]} data-label={table.head[i + 1]}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {item.steps && <ol className="steps">{item.steps.map((s) => <li key={s}>{s}</li>)}</ol>}
      {item.points && <ul className="points">{item.points.map((p) => <li key={p}>{p}</li>)}</ul>}
      {item.example && <p className="example"><strong>Example:</strong> {item.example}</p>}
      {item.note && <p>{item.note}</p>}
    </div>
  );
}

function Home() {
  return (
    <>
      <a className="skip" href="#main">Skip to content</a>
      <header className="top">
        <div className="wrap top-inner">
          <a className="brand" href="#top">
            <Logo />
            <span>{project.name}</span>
          </a>
          <nav aria-label="Sections">
            {nav.map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}
            <Link to="/contact">Contact</Link>
          </nav>
          <a className="pill" href={project.liveUrl}>Open SkillBridge</a>
        </div>
      </header>

      <main id="main">
        <div id="top" className="wrap hero">
          <div>
            <p className="hero-kicker">{project.name} research project</p>
            <h1>{project.title}</h1>
            <p className="lead">{project.summary}</p>
            <p className="hero-actions">
              <a className="pill" href={project.liveUrl}>Open SkillBridge</a>
              <a className="pill pill-quiet" href="#scope">Read the project scope</a>
            </p>
          </div>
          <Loop />
        </div>

        <dl className="wrap facts">
          {project.facts.map(([k, v]) => (
            <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
          ))}
        </dl>

        <Section id="introduction" title="Introduction">
          {introduction.map((p) => <p key={p}>{p}</p>)}
        </Section>

        <Section id="scope" title="Project scope">
          <h3>Research gap</h3>
          <p>{researchGap.lead}</p>
          <ul className="gap-list">
            {researchGap.points.map(([what, lacks]) => (
              <li key={what}><strong>{what}</strong> {lacks}</li>
            ))}
          </ul>
          <p>{researchGap.close}</p>

          <h3>Research problem</h3>
          <p>{problem}</p>

          <h3>Proposed solution</h3>
          <p>{solution}</p>

          <h3>Research objective</h3>
          <p className="objective">{mainObjective}</p>

          <h3>Components, sub-objectives and novelty</h3>
          <p>The main objective is delivered through five components. Four are owned by one team member each, and the analytics layer that joins them is shared.</p>
          <div className="components">
            {components.map((item) => (
              <article key={item.id} id={item.id} className="component">
                <header>
                  <h4>{item.name}</h4>
                  <p>{item.owner}</p>
                </header>
                <dl>
                  <dt>Sub-objective</dt><dd>{item.subObjective}</dd>
                  <dt>How it works</dt><dd>{item.how}</dd>
                  <dt>Novelty</dt><dd>{item.novelty}</dd>
                </dl>
              </article>
            ))}
          </div>
        </Section>

        <Section id="methodology" title="Methodology">
          <p>The five components are connected as one continuous process. Each produces an output the next one uses, so both cognitive and emotional information shape the learner’s pathway.</p>

          <div className="methods">
            <nav className="method-nav" aria-label="Methods">
              {methodology.groups.map((g) => <a key={g.id} href={`#${g.id}`}>{g.nav}</a>)}
            </nav>
            {methodology.groups.map((g) => (
              <section key={g.id} id={g.id} className="method-group" aria-labelledby={`${g.id}-title`}>
                <h3 id={`${g.id}-title`}>{g.title}</h3>
                {g.kicker && <p className="when">{g.kicker}</p>}
                <p>{g.intro}</p>
                <div className="formulas">
                  {(figures[g.id] || []).map((f) => <Figure key={f.title} figure={f} />)}
                  {g.items.map((item) => <Method key={item.title} item={item} />)}
                </div>
              </section>
            ))}
            <div className="formulas method-group">
              <Method item={methodology.limits} />
            </div>
          </div>

          <h3>System architecture</h3>
          <p>{architectureNote}</p>
          <div className="formulas">
            {figures.architecture.map((f) => <Figure key={f.title} figure={f} />)}
          </div>
          <div className="table-scroll">
            <table>
              <thead><tr><th>Service</th><th>Built with</th><th>What it does</th></tr></thead>
              <tbody>
                {services.map(([name, stack, role]) => (
                  <tr key={name}><th scope="row">{name}</th><td>{stack}</td><td>{role}</td></tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>Evaluation so far</h3>
          <div className="formulas">
            {figures.evaluation.map((f) => <Figure key={f.title} figure={f} />)}
          </div>
          <div className="table-scroll">
            <table>
              <thead><tr><th>Component</th><th>Tested on</th><th>Compared model</th><th>Selected model</th></tr></thead>
              <tbody>
                {evaluation.rows.map(([name, data, other, chosen]) => (
                  <tr key={name}><th scope="row">{name}</th><td>{data}</td><td>{other}</td><td>{chosen}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="caveat">{evaluation.caveat}</p>
        </Section>

        <Section id="milestones" title="Milestones">
          <ol className="timeline">
            {milestones.map((m) => (
              <li key={m.name}>
                <h3>{m.name}</h3>
                {m.when && <p className="when">{m.when}</p>}
                <p>{m.detail}</p>
              </li>
            ))}
          </ol>
        </Section>

        <Section id="about" title="About us">
          <p>We are four final-year undergraduates at the {affiliation}.</p>
          <ul className="team">
            {team.map((m) => (
              <li key={m.id}>
                <img src={m.photo} alt={m.name} width="352" height="352" loading="lazy" />
                <div>
                  <h3>{m.name}</h3>
                  <p>{m.id}</p>
                  <p className="role">{m.role}</p>
                  {m.also && <p>{m.also}</p>}
                  <a href={`mailto:${m.email}`}>{m.email}</a>
                </div>
              </li>
            ))}
          </ul>

          <h3>Supervised by</h3>
          <ul className="supervisors">
            {supervisors.map((s) => (
              <li key={s.email}>
                <h4>{s.name}</h4>
                <p>{s.role}</p>
                <a href={`mailto:${s.email}`}>{s.email}</a>
              </li>
            ))}
          </ul>
        </Section>
      </main>

      <footer className="foot">
        <div className="wrap foot-inner">
          <p><strong>{project.name}</strong>, project R26-IT-033</p>
          <p>{affiliation}</p>
        </div>
        <p className="foot-links">
          <a href={project.liveUrl}>slskillbridgex.com</a>
          <a href={project.documentsUrl} target="_blank" rel="noopener noreferrer">Project documents</a>
        </p>
      </footer>
    </>
  );
}

function ContactPage() {
  return (
    <>
      <a className="skip" href="#main">Skip to content</a>
      <header className="top">
        <div className="wrap top-inner">
          <Link className="brand" to="/">
            <Logo />
            <span>{project.name}</span>
          </Link>
          <Link className="pill pill-quiet" to="/">Back to site</Link>
        </div>
      </header>
      <Contact />
    </>
  );
}

// The future flags opt in to React Router 7 behaviour and keep its upgrade warnings out of the console.
function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/contact" element={<ContactPage />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
